"use client";

// ============================================================================
// AdminCopiesButton
//
// ADMIN-ONLY insight on a trader profile: "who does this trader copy?".
// Gated to the Mirror allowlist (useMirrorAccess — frenflowdev / predictfren /
// zeroxitadori). Renders nothing for everyone else. Clicking opens a modal
// listing the leaders the profile's owner copies (Copia a / Estado / Modo),
// each row linking to that leader's profile. Backend (admin-gated) does the
// wallet → FrenFlow user → copytrading_user_leaders resolution.
//
// A trader profile is keyed by a Polymarket wallet; many such wallets aren't
// FrenFlow users → the modal shows an empty state. Polymarket-only (Kalshi /
// Predict copytrading isn't keyed by these wallets).
// ============================================================================

import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { FiEye, FiX, FiCheckCircle, FiPauseCircle, FiXCircle } from "react-icons/fi";
import { authFetch } from "@/lib/authFetch";
import { useMirrorAccess } from "@/hooks/useMirrorAccess";
import { createLogger } from "@/lib/logger";

const logger = createLogger("AdminCopiesButton");

interface LeaderRecent {
  d1PnlUsd: number | null;
  d7PnlUsd: number | null;
  d7RoiPct: number | null;
  allTimeRoiPct: number | null;
  allTimeWinRatePct: number | null;
  allTimeTrades: number | null;
}

interface CopyRow {
  leaderUsername: string | null;
  leaderWallet: string | null;
  leaderImage: string | null;
  copyEnabled: boolean;
  copyMode: string | null;
  baseStakeUsd: number | null;
  stakePercentage: number | null;
  // PnL del owner copiando a este leader (live desde positions).
  ownerRealizedPnlUsd?: number;
  ownerRealizedPct?: number | null;
  ownerOpenPnlUsd?: number;
  ownerClosedPositions?: number;
  ownerOpenPositions?: number;
  // Rendimiento propio del leader (Predexon; puede faltar).
  leaderRecent?: LeaderRecent | null;
  // true = copió a este leader alguna vez pero YA NO lo sigue (histórico).
  unfollowed?: boolean;
}

interface CopiesResponse {
  found: boolean;
  owner: { username: string | null; telegram_username: string | null } | null;
  copies: CopyRow[];
}

function modeLabel(c: CopyRow): string {
  if (c.copyMode === "fixed") return `Fixed $${c.baseStakeUsd ?? 0}`;
  if (c.copyMode === "percentage") return `${c.stakePercentage ?? 0}%`;
  if (c.copyMode === "mirror" || c.copyMode === "mirror_percentage") return "Mirror";
  return c.copyMode || "—";
}

// $ con signo. null/undefined → "—".
function fmtUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  const abs = Math.abs(v);
  return `${sign}$${abs >= 100 ? Math.round(abs).toLocaleString("en-US") : abs.toFixed(2)}`;
}

// % con signo. null/undefined → "—".
function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}${Math.abs(v).toFixed(Math.abs(v) >= 100 ? 0 : 1)}%`;
}

// Color verde/rojo/neutro según signo (Tailwind classes).
function pnlColor(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v === 0) return "text-gray-400 dark:text-gray-500";
  return v > 0 ? "text-[#00C781] dark:text-[#00FF9D]" : "text-red-500 dark:text-red-400";
}

export default function AdminCopiesButton({ wallet }: { wallet: string | null | undefined }) {
  const { hasMirrorAccess } = useMirrorAccess();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CopiesResponse | null>(null);

  const load = useCallback(async () => {
    if (!wallet) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/api/copytrading/admin/copies/${wallet}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      logger.error("Failed to load copies", { error: err instanceof Error ? err.message : String(err) });
      setError("No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  if (!hasMirrorAccess || !wallet) return null;

  const openModal = () => {
    setOpen(true);
    if (!data) void load();
  };

  const copies = data?.copies ?? [];
  const ownerLabel = data?.owner?.username || data?.owner?.telegram_username || null;

  return (
    <>
      {/* Admin button — sits next to the Copy/Copying CTA */}
      <button
        onClick={openModal}
        title="Admin: a quién copia este trader"
        className="font-inter-tight w-full py-2.5 mb-3 bg-gray-100 dark:bg-[#0a0a0b] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300 font-semibold text-sm rounded-[5px] flex items-center justify-center gap-2 hover:bg-gray-50 dark:hover:bg-[#0a0a0b]/50 transition-colors"
      >
        <FiEye className="w-4 h-4" />
        Who Follows <span className="font-normal text-gray-400">(ADMIN)</span>
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col bg-white dark:bg-[#0a0a0b] border border-gray-200 dark:border-[#1A1A1A] rounded-[8px] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-[#1A1A1A]">
              <div className="min-w-0">
                <h3 className="font-inter-tight font-bold text-gray-900 dark:text-white text-sm">Who Follows <span className="font-normal text-gray-400">(ADMIN)</span></h3>
                {ownerLabel && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">@{ownerLabel}</p>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-[5px] text-gray-400 hover:bg-gray-100 dark:hover:bg-[#18181D] transition-colors"
                aria-label="Cerrar"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Body — cap a ~5 filas y luego scroll vertical (cada fila ~92px).
                min() para que en pantallas bajas no se pase del viewport. */}
            <div className="overflow-y-auto max-h-[min(68vh,460px)]">
              {loading ? (
                <div className="p-6 text-center text-sm text-gray-400">Cargando…</div>
              ) : error ? (
                <div className="p-6 text-center text-sm text-red-500">{error}</div>
              ) : copies.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-400">
                  {data && !data.found
                    ? "Este trader no es un usuario de FrenFlow."
                    : "No copia a ningún trader."}
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-[#141414]">
                  {copies.map((c, i) => {
                    const clickable = !!c.leaderWallet;
                    const r = c.leaderRecent;
                    const name = c.leaderUsername || (c.leaderWallet ? `${c.leaderWallet.slice(0, 6)}…${c.leaderWallet.slice(-4)}` : "—");
                    return (
                      <li key={i}>
                        <div
                          onClick={clickable ? () => { setOpen(false); router.push(`/traders/${c.leaderWallet}`); } : undefined}
                          className={`px-4 py-3 transition-colors ${clickable ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0a0a0b]/50" : ""}`}
                        >
                          {/* Línea 1: nombre + estado */}
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-inter-tight font-semibold text-gray-900 dark:text-white truncate">{name}</span>
                            {c.unfollowed ? (
                              <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                                <FiXCircle className="w-3.5 h-3.5" /> ya no copia
                              </span>
                            ) : c.copyEnabled ? (
                              <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-[#00C781] dark:text-[#00FF9D]">
                                <FiCheckCircle className="w-3.5 h-3.5" /> activo
                              </span>
                            ) : (
                              <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-gray-400">
                                <FiPauseCircle className="w-3.5 h-3.5" /> pausado
                              </span>
                            )}
                          </div>

                          {/* Línea 2: PnL del owner copiando a este leader + modo */}
                          <div className="mt-1.5 flex items-center justify-between gap-2 text-xs">
                            <span className="text-gray-500 dark:text-gray-400 truncate">
                              Tu PnL{" "}
                              <span className={`font-semibold ${pnlColor(c.ownerRealizedPnlUsd)}`}>{fmtUsd(c.ownerRealizedPnlUsd)}</span>
                              {c.ownerRealizedPct != null && (
                                <span className={pnlColor(c.ownerRealizedPnlUsd)}> ({fmtPct(c.ownerRealizedPct)})</span>
                              )}
                              {(c.ownerClosedPositions ?? 0) > 0 && (
                                <span className="text-gray-400"> · {c.ownerClosedPositions} cerradas</span>
                              )}
                            </span>
                            <span className="shrink-0 text-gray-600 dark:text-gray-300">{modeLabel(c)}</span>
                          </div>

                          {/* Línea 3: rendimiento PROPIO del leader (decidir copiarlo) */}
                          <div className="mt-1 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                            <span className="uppercase tracking-wide text-[10px] text-gray-400">Leader</span>
                            {r ? (
                              <>
                                <span>24h <span className={`font-medium ${pnlColor(r.d1PnlUsd)}`}>{fmtUsd(r.d1PnlUsd)}</span></span>
                                <span>7d <span className={`font-medium ${pnlColor(r.d7PnlUsd)}`}>{fmtUsd(r.d7PnlUsd)}</span></span>
                                {r.allTimeRoiPct != null && (
                                  <span>ROI <span className={`font-medium ${pnlColor(r.allTimeRoiPct)}`}>{fmtPct(r.allTimeRoiPct)}</span></span>
                                )}
                                {r.allTimeWinRatePct != null && <span>{r.allTimeWinRatePct.toFixed(0)}% win</span>}
                                {r.allTimeTrades != null && <span>{r.allTimeTrades.toLocaleString("en-US")} trades</span>}
                              </>
                            ) : (
                              <span className="text-gray-400">sin datos recientes</span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
